import { decode } from 'base-64';
import type { KadeConfig } from '@kade-technologies/web-sdk';
import type KadeSDKInstance from '@kade-technologies/web-sdk';
import { apiConfig } from '@bonanza-social/toolkit/api';
import Config from '@bonanza-social/toolkit/config';
import {
  appAuthenticated,
  appOpened,
  startListening,
} from '@bonanza-social/toolkit/state';
import { logError } from '@bonanza-social/toolkit/logging';
import { BottomSheet } from '@bonanza-social/navigation';
import {
  locationSheetAnswered,
  loginPingNotSent,
  loginPingSent,
  loginPingStarted,
} from './geoFence.slice';
import {
  getLocationPermission,
  LocationPermission,
} from '../utils/locationPermission';
import { withTimeout } from '../utils/withTimeout';
import { PING_TIMEOUT, SDK_INIT_TIMEOUT } from '../constants';

let geoFenceSdkPromise: Promise<KadeSDKInstance | undefined> | undefined;

const initGeoFence = async (): Promise<KadeSDKInstance | undefined> => {
  if (!Config.GEOFENCE_CONFIG) {
    return undefined;
  }

  let geoFenceConfig: KadeConfig;

  try {
    geoFenceConfig = JSON.parse(decode(Config.GEOFENCE_CONFIG));
  } catch (error) {
    logError('GeoFence config parse error', error);
    return undefined;
  }

  try {
    // Loaded on demand so the SDK isn't part of the main bundle
    const { default: KadeSDK } = await import('@kade-technologies/web-sdk');

    const sdk = new KadeSDK(geoFenceConfig);

    await sdk.init();

    return sdk;
  } catch (error) {
    logError('GeoFence init error', error);
    geoFenceSdkPromise = undefined;
    return undefined;
  }
};

const getGeoFenceSdk = () => {
  geoFenceSdkPromise ??= initGeoFence();

  return geoFenceSdkPromise;
};

// Fire and forget: the frontend already hides content for this check
const sendPingWithoutLocation = async (userId: string) => {
  try {
    const sdk = await withTimeout(
      getGeoFenceSdk(),
      SDK_INIT_TIMEOUT,
      'GeoFence init',
    );

    await sdk?.ping({ userId, silent: true, skipLocation: true });
  } catch (error) {
    logError('Kade no-location ping error', error);
  }
};

startListening({
  actionCreator: appOpened,
  effect: async () => {
    await getGeoFenceSdk();
  },
});

startListening({
  actionCreator: appAuthenticated,
  effect: async (_, { dispatch, take }) => {
    if (!Config.GEOFENCE_CONFIG) {
      return;
    }

    const userId = await apiConfig.getUserId();

    if (!userId) {
      return;
    }

    dispatch(loginPingStarted());

    let permission: LocationPermission | 'later' =
      await getLocationPermission();

    if (permission === 'prompt') {
      BottomSheet.open({
        namespace: 'geoFence',
        type: 'default',
        showCloseIcon: false,
        closeOnBlur: false,
        enablePanDownToClose: false,
        autoCloseOnNavigation: false,
      });

      const [answer] = await take(locationSheetAnswered.match);
      permission = answer.payload;
    }

    if (permission !== 'granted') {
      dispatch(loginPingNotSent());

      sendPingWithoutLocation(userId.toString());
      return;
    }

    let geoFenceSdk: KadeSDKInstance | undefined;

    try {
      geoFenceSdk = await withTimeout(
        getGeoFenceSdk(),
        SDK_INIT_TIMEOUT,
        'GeoFence init',
      );
    } catch (error) {
      logError('GeoFence init error', error);
    }

    if (!geoFenceSdk) {
      dispatch(loginPingNotSent());
      return;
    }

    try {
      await withTimeout(
        geoFenceSdk.ping({
          userId: userId.toString(),
          silent: true,
          skipLocation: false,
        }),
        PING_TIMEOUT,
        'GeoFence ping',
      );

      dispatch(loginPingSent());
    } catch (error) {
      logError('GeoFence ping error', error);
      dispatch(loginPingNotSent());
    }
  },
});
